import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "react-toastify";
import { ChevronDown, CalendarIcon, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Header from "@/components/Header";
import CountrySelectDialog, {
  countries,
} from "@/components/CountrySelectDialog";
import { oxpayAPI } from "@/lib/oxpay";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { countryCodeMap } from "@/constants/countryCodeMap";

export default function EditUser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [formData, setFormData] = useState({
    email: user?.email || "",
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    gender: "",
    birthday: "",
    placeOfBirth: "",
    maritalStatus: "",
    idType: "passport",
    idNumber: "",
    idDateIssue: "",
    idDateExpiry: "",
    countryCode: "",
    countryOfIssue: "",
    phoneNumber: "",
    // Business fields
    natureOfBusiness: "",
    purposeOfAccount: "",
    employerName: "",
    employmentType: "",
    sourceOfFunds: "",
    occupation: "",
    volumeOfAssets: "",
  });

  // Load existing account data from OxPay API
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts

    const loadAccountData = async () => {
      if (!user?.email || !isMounted) return;

      setIsLoadingData(true);

      try {
        // First get account info from local DB to get oxpay_user_id
        const { data: localData, error: localError } = await supabase
          .from("accounts")
          .select("*")
          .eq("email", user.email)
          .single();

        if (!isMounted) return;

        if (!localData || localError) {
          toast.error("No account data found. Please register first.");
          navigate("/register-user");
          return;
        }

        setAccountData(localData);

        // Then fetch latest data from OxPay API
        if (localData.oxpay_user_id) {
          try {
            const oxpayResponse = await oxpayAPI.getUser(
              localData.oxpay_user_id
            );

            if (!isMounted) return;

            console.log("OxPay user data:", oxpayResponse);

            if (oxpayResponse?.body) {
              const oxpayData = oxpayResponse.body;

              // Helper function to parse date strings to YYYY-MM-DD format
              const formatDateToYYYYMMDD = (dateString) => {
                if (!dateString) return "";
                try {
                  const date = new Date(dateString);
                  if (isNaN(date.getTime())) return "";
                  return date.toISOString().split("T")[0];
                } catch {
                  return "";
                }
              };

              // Auto-fill form with OxPay data (source of truth)
              setFormData((prev) => ({
                ...prev,
                firstName: oxpayData.name?.first || "",
                middleName: oxpayData.name?.middle || "",
                lastName: oxpayData.name?.last || "",
                preferredName: oxpayData.name?.preferred || "",
                title: oxpayData.title || "",
                gender: oxpayData.gender || "",
                birthday: formatDateToYYYYMMDD(oxpayData.birthday) || "",
                placeOfBirth: localData.place_of_birth || "",
                countryOfIssue: localData.id_country_of_issue || "",
                idType: oxpayData.identification?.type || "passport",
                idNumber: oxpayData.identification?.number || "",
                idDateIssue:
                  formatDateToYYYYMMDD(oxpayData.identification?.issue) || "",
                idDateExpiry:
                  formatDateToYYYYMMDD(oxpayData.identification?.expiry) || "",
                maritalStatus: oxpayData.marital_status || "",
                phoneNumber: oxpayData.mobile?.number || "",
                // Business fields from local DB (not in OxPay)
                natureOfBusiness: localData.nature_of_business || "",
                purposeOfAccount: localData.purpose_of_account || "",
                employerName: localData.employer_name || "",
                employmentType: localData.employment_type || "",
                sourceOfFunds: localData.source_of_funds || "",
                occupation: localData.occupation || "",
                volumeOfAssets: localData.volume_of_assets || "",
              }));

              // Set selected country based on OxPay mobile data
              if (oxpayData.mobile?.country_code) {
                const country = countries.find(
                  (c) =>
                    c.mobileCode.replace("+", "") ===
                    oxpayData.mobile.country_code
                );
                if (country) {
                  setSelectedCountry(country);
                  setFormData((prev) => ({
                    ...prev,
                    countryCode: country.code,
                    countryOfIssue: country.code,
                  }));
                }
              }
            }
          } catch (oxpayError) {
            console.error("Error loading OxPay data:", oxpayError);
            toast.error("Failed to load latest user data from OxPay");
          }
        } else {
          toast.error("OxPay account not found. Please register first.");
          navigate("/register-user");
        }
      } catch (error) {
        console.error("Error loading account data:", error);
        toast.error("Failed to load account data");
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    };

    loadAccountData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [user?.email]); // Only depend on user.email, not the whole user object

  // Format phone number based on country
  const formatPhoneNumber = (value, country) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");

    if (!country) return cleaned;

    // Apply country-specific formatting
    let formatted = cleaned;
    switch (country.code) {
      case "US":
      case "CA":
        // Format: 123 456 7890
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "KR":
        // Format: 10 1234 5678
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "JP":
        // Format: 90 1234 5678
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "GB":
        // Format: 7911 123456
        if (cleaned.length <= 4) formatted = cleaned;
        else formatted = `${cleaned.slice(0, 4)} ${cleaned.slice(4, 10)}`;
        break;
      case "AU":
        // Format: 412 345 678
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 9)}`;
        break;
      case "KH":
        // Format: 12 345 678 (Cambodia)
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 5)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            5
          )} ${cleaned.slice(5, 8)}`;
        break;
      case "PH":
        // Format: 905 123 4567 (Philippines)
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      default:
        // Default format: groups of 3-4 digits
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 7)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            7
          )} ${cleaned.slice(7)}`;
    }

    return formatted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      // Format phone number automatically
      const formatted = formatPhoneNumber(value, selectedCountry);
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
    } else if (name === "idNumber") {
      // For ID number, only allow alphanumeric characters
      const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, "");
      // Limit to 20 characters
      const limited = alphanumericOnly.slice(0, 20).toUpperCase();
      setFormData((prev) => ({
        ...prev,
        [name]: limited,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData((prev) => ({
      ...prev,
      countryCode: country.code,
      countryOfIssue: country.code, // 여권 발급 국가도 함께 설정
      phoneNumber: prev.phoneNumber,
    }));
    setCountryDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields (middleName is optional, preferredName is read-only)
    const requiredFields = {
      title: "Title",
      firstName: "First Name",
      lastName: "Last Name",
      gender: "Gender",
      birthday: "Date of Birth",
      placeOfBirth: "Place of Birth",
      maritalStatus: "Marital Status",
      idNumber: "ID Number",
      idDateIssue: "ID Issue Date",
      idDateExpiry: "ID Expiry Date",
      countryOfIssue: "Country of Issue",
      employmentType: "Employment Type",
      occupation: "Occupation",
      employerName: "Employer Name",
      natureOfBusiness: "Nature of Business",
      purposeOfAccount: "Purpose of Account",
      sourceOfFunds: "Source of Funds",
      volumeOfAssets: "Volume of Assets",
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        toast.error(`${label} is required`);
        return;
      }
    }

    if (!selectedCountry) {
      toast.error("Please select a country");
      return;
    }

    if (!formData.phoneNumber) {
      toast.error("Phone number is required");
      return;
    }

    setIsLoading(true);

    try {
      if (!accountData?.oxpay_user_id) {
        toast.error(
          "OxPay account not found. Please complete registration first."
        );
        navigate("/register-user");
        return;
      }

      // Map nationality values for OxPay
      const nationalityMap = {
        KR: "South Korean",
        US: "American",
        JP: "Japanese",
        CN: "Chinese",
        GB: "British",
        CA: "Canadian",
        AU: "Australian",
        // Add more as needed
      };

      // Helper function to ensure date is in YYYY-MM-DD format
      const ensureDateFormat = (dateString) => {
        if (!dateString) return null;
        try {
          // If already in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
          }
          // Otherwise parse and format
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return null;
          return date.toISOString().split("T")[0];
        } catch {
          return null;
        }
      };

      // Prepare update fields for OxPay (same structure as RegisterUser)
      const updateFields = {
        //email: user.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        mobile: formData.phoneNumber.replace(/\s/g, ""),
        mobile_country_code: selectedCountry.mobileCode.replace("+", ""),
        middle_name: formData.middleName || null,
        //preferred_name: formData.preferredName || null,
        birthday: ensureDateFormat(formData.birthday),
        id_number: formData.idNumber,
        id_type: formData.idType,
        id_date_expiry: ensureDateFormat(formData.idDateExpiry),
        id_date_issue: ensureDateFormat(formData.idDateIssue),
        country_of_issue:
          countryCodeMap[formData.countryOfIssue || selectedCountry.code] || "",
        gender: formData.gender,
        title: formData.title,
        place_of_birth: formData.placeOfBirth || null,
        nationality: nationalityMap[selectedCountry.code] || "",
        marital_status: formData.maritalStatus,
        nature_of_business: formData.natureOfBusiness || null,
        purpose_of_account: formData.purposeOfAccount || null,
        employer_name: formData.employerName || null,
        employment_type: formData.employmentType || null,
        source_of_funds: formData.sourceOfFunds || null,
        occupation: formData.occupation || null,
        volume_of_assets: formData.volumeOfAssets || null,
      };

      // Update OxPay user
      if (Object.keys(updateFields).length > 0) {
        await oxpayAPI.updateUser(accountData.oxpay_user_id, updateFields);
      }

      // Update local database
      const { error: dbError } = await supabase
        .from("accounts")
        .update({
          title: formData.title || null,
          first_name: formData.firstName || null,
          middle_name: formData.middleName || null,
          last_name: formData.lastName || null,
          preferred_name: formData.preferredName || null,
          gender: formData.gender || null,
          birthday: ensureDateFormat(formData.birthday),
          place_of_birth: formData.placeOfBirth || null,
          marital_status: formData.maritalStatus || null,
          id_type: formData.idType,
          id_number: formData.idNumber || null,
          id_date_issue: ensureDateFormat(formData.idDateIssue),
          id_date_expiry: ensureDateFormat(formData.idDateExpiry),
          id_country_of_issue: formData.countryOfIssue || null,
          phone_number:
            selectedCountry && formData.phoneNumber
              ? `${selectedCountry.mobileCode}${formData.phoneNumber}`
              : accountData.phone_number,
          nationality:
            nationalityMap[selectedCountry?.code] || accountData.nationality,
          // Business fields
          nature_of_business: formData.natureOfBusiness || null,
          purpose_of_account: formData.purposeOfAccount || null,
          employer_name: formData.employerName || null,
          employment_type: formData.employmentType || null,
          source_of_funds: formData.sourceOfFunds || null,
          occupation: formData.occupation || null,
          volume_of_assets: formData.volumeOfAssets || null,
        })
        .eq("email", user.email);

      if (dbError) throw dbError;

      toast.success("Profile updated successfully!");
      navigate("/home");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />
      <div className="flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md overflow-hidden">
          <CardHeader>
            <CardTitle>Edit Profile</CardTitle>
            <CardDescription>Update your personal information</CardDescription>

            {/* Warning Message */}
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ Modifying and saving your profile will trigger a re-review
                process and may result in temporary account suspension.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title and Gender */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="title">Title</Label>
                  <Select
                    name="title"
                    value={formData.title}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, title: value }))
                    }
                  >
                    <SelectTrigger id="title">
                      <SelectValue placeholder="Select title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">Mr</SelectItem>
                      <SelectItem value="Mrs">Mrs</SelectItem>
                      <SelectItem value="Miss">Miss</SelectItem>
                      <SelectItem value="Ms">Ms</SelectItem>
                      <SelectItem value="Dr">Dr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, gender: value }))
                    }
                  >
                    <SelectTrigger id="gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Birthday and Place of Birth */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="birthday">Date of Birth</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="birthday"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.birthday && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.birthday || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.birthday
                            ? new Date(formData.birthday)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            birthday: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="placeOfBirth">Place of Birth</Label>
                  <Input
                    id="placeOfBirth"
                    name="placeOfBirth"
                    placeholder="City / Country"
                    value={formData.placeOfBirth}
                    onChange={handleInputChange}
                    className="pl-2"
                  />
                </div>
              </div>

              {/* Marital Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="maritalStatus">Marital Status</Label>
                  <Select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, maritalStatus: value }))
                    }
                  >
                    <SelectTrigger id="maritalStatus">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                      <SelectItem value="NA">NA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
              </div>

              {/* Names */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={50}
                    title="Only English letters and spaces allowed"
                    className="pl-2"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={50}
                    title="Only English letters and spaces allowed"
                    className="pl-2"
                  />
                </div>
              </div>

              {/* Middle Name and Preferred Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="middleName">Middle Name (Optional)</Label>
                  <Input
                    id="middleName"
                    name="middleName"
                    placeholder="Middle name"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]*$"
                    maxLength={50}
                    title="Only English letters and spaces allowed"
                    className="pl-2"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="preferredName">
                    Preferred Name (Read Only)
                  </Label>
                  <Input
                    id="preferredName"
                    name="preferredName"
                    placeholder="Preferred name"
                    disabled={true}
                    value={formData.preferredName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={25}
                    title="Only English letters and spaces allowed"
                    className="pl-2"
                  />
                </div>
              </div>

              {/* ID Type and Number */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="idType">ID Type</Label>
                  <Select
                    name="idType"
                    value={formData.idType}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, idType: value }))
                    }
                  >
                    <SelectTrigger id="idType">
                      <SelectValue placeholder="Select ID type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="nric">NRIC or Driver's ID</SelectItem>
                      <SelectItem value="ep">Employee Pass</SelectItem>
                      <SelectItem value="sp">Student Pass</SelectItem>
                      <SelectItem value="wp">Work Pass</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="idNumber">ID Number</Label>
                  <Input
                    id="idNumber"
                    name="idNumber"
                    placeholder={
                      formData.idType === "passport"
                        ? "AB1234567"
                        : "Enter ID number"
                    }
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z0-9]*$"
                    maxLength={20}
                    title="Only letters and numbers allowed (max 20 characters)"
                    className="pl-4 uppercase"
                  />
                </div>
              </div>
              {formData.idNumber && formData.idNumber.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Format: Letters and numbers only
                </p>
              )}

              {/* ID Dates (swapped) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="idDateIssue">ID Issue Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="idDateIssue"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.idDateIssue && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.idDateIssue || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.idDateIssue
                            ? new Date(formData.idDateIssue)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            idDateIssue: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="idDateExpiry">ID Expiry Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="idDateExpiry"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.idDateExpiry && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.idDateExpiry || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.idDateExpiry
                            ? new Date(formData.idDateExpiry)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            idDateExpiry: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) => date < new Date("1900-01-01")}
                        captionLayout="dropdown"
                        fromYear={new Date().getFullYear()}
                        toYear={new Date().getFullYear() + 30}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-3 pt-4">
                <h3 className="text-sm font-medium text-foreground">
                  Contact Information
                </h3>

                <div className="space-y-1">
                  <Label htmlFor="countryCode">Country</Label>
                  <button
                    type="button"
                    onClick={() => setCountryDialogOpen(true)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left border rounded-md hover:bg-accent transition-colors"
                  >
                    <span
                      className={selectedCountry ? "" : "text-muted-foreground"}
                    >
                      {selectedCountry
                        ? `${selectedCountry.name} (${selectedCountry.code})`
                        : "Select your country"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <div className="flex items-center gap-2">
                    {selectedCountry && (
                      <div className="flex items-center h-12 px-3 bg-muted rounded-md border">
                        <span className="text-sm font-medium">
                          {selectedCountry.mobileCode}
                        </span>
                      </div>
                    )}
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      placeholder={
                        selectedCountry
                          ? selectedCountry.placeholder
                          : "Select country first"
                      }
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      disabled={!selectedCountry}
                      className="flex-1 pl-4"
                    />
                  </div>
                  {!selectedCountry && (
                    <p className="text-xs text-muted-foreground">
                      Please select your country first
                    </p>
                  )}
                </div>
              </div>

              {/* Business Information Section - Gap before this section */}
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3">
                  Business Information
                </h3>

                {/* Employment Type and Occupation */}
                <div className="grid grid-cols-1 gap-4 mb-3">
                  <div className="space-y-1">
                    <Label htmlFor="employmentType">Employment Type</Label>
                    <Select
                      name="employmentType"
                      value={formData.employmentType}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          employmentType: value,
                        }))
                      }
                    >
                      <SelectTrigger id="employmentType">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed">Employed</SelectItem>
                        <SelectItem value="self_employed">
                          Self-employed
                        </SelectItem>
                        <SelectItem value="unemployed">Unemployed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="occupation">Occupation</Label>
                    <Select
                      value={formData.occupation}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          occupation: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select occupation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accountant">Accountant</SelectItem>
                        <SelectItem value="actor">Actor</SelectItem>
                        <SelectItem value="advertising_and_public_relations_manager">
                          Advertising and Public Relations Manager
                        </SelectItem>
                        <SelectItem value="army">Army</SelectItem>
                        <SelectItem value="artist_group_performance">
                          Artist (Group Performance)
                        </SelectItem>
                        <SelectItem value="athlete_and_sports_player">
                          Athlete and Sports Player
                        </SelectItem>
                        <SelectItem value="bank_teller_and_related_clerk">
                          Bank Teller and Related Clerk
                        </SelectItem>
                        <SelectItem value="barber">Barber</SelectItem>
                        <SelectItem value="bartender">Bartender</SelectItem>
                        <SelectItem value="bus_and_tram_driver">
                          Bus and Tram Driver
                        </SelectItem>
                        <SelectItem value="chef">Chef</SelectItem>
                        <SelectItem value="civil_defence_associate_professional">
                          Civil Defence Associate Professional
                        </SelectItem>
                        <SelectItem value="cleaner">Cleaner</SelectItem>
                        <SelectItem value="cleaning_and_housekeeping_supervisor_in_office">
                          Cleaning and Housekeeping Supervisor in Office
                        </SelectItem>
                        <SelectItem value="construction_supervisor">
                          Construction Supervisor
                        </SelectItem>
                        <SelectItem value="construction_worker">
                          Construction Worker
                        </SelectItem>
                        <SelectItem value="cook">Cook</SelectItem>
                        <SelectItem value="dancer_and_choreographer">
                          Dancer and Choreographer
                        </SelectItem>
                        <SelectItem value="domestic_housekeeper">
                          Domestic Housekeeper
                        </SelectItem>
                        <SelectItem value="domestic_worker">
                          Domestic Worker
                        </SelectItem>
                        <SelectItem value="driver">Driver</SelectItem>
                        <SelectItem value="early_childhood_educator">
                          Early Childhood Educator
                        </SelectItem>
                        <SelectItem value="engineering_professional_excluding_electrotechnology_not_elsewhere_classified">
                          Engineering Professional (Excluding Electrotechnology)
                          not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="farm_worker">Farm Worker</SelectItem>
                        <SelectItem value="fast_food_staff">
                          Fast Food Staff
                        </SelectItem>
                        <SelectItem value="field_crop_grower">
                          Field Crop Grower
                        </SelectItem>
                        <SelectItem value="film_staff">Film Staff</SelectItem>
                        <SelectItem value="finance_manager_agriculture">
                          Finance Manager - Agriculture
                        </SelectItem>
                        <SelectItem value="finance_manager_construction">
                          Finance Manager - Construction
                        </SelectItem>
                        <SelectItem value="finance_manager_manufacturing">
                          Finance Manager - Manufacturing
                        </SelectItem>
                        <SelectItem value="finance_manager_others">
                          Finance Manager - Others
                        </SelectItem>
                        <SelectItem value="finance_manager_plantation">
                          Finance Manager - Plantation
                        </SelectItem>
                        <SelectItem value="finance_manager_services">
                          Finance Manager - Services
                        </SelectItem>
                        <SelectItem value="financial_analyst">
                          Financial Analyst
                        </SelectItem>
                        <SelectItem value="financial_and_investment_adviser">
                          Financial and Investment Adviser
                        </SelectItem>
                        <SelectItem value="fire_fighter">
                          Fire Fighter
                        </SelectItem>
                        <SelectItem value="fishery_and_aquaculture_labourer">
                          Fishery and Aquaculture Labourer
                        </SelectItem>
                        <SelectItem value="fitness_and_recreation_instructor_and_programme_leader">
                          Fitness and Recreation Instructor and Programme Leader
                        </SelectItem>
                        <SelectItem value="forestry_labourer">
                          Forestry Labourer
                        </SelectItem>
                        <SelectItem value="garden_and_horticultural_labourer">
                          Garden and Horticultural Labourer
                        </SelectItem>
                        <SelectItem value="gardener">Gardener</SelectItem>
                        <SelectItem value="hairdresser_or_salon_staff">
                          Hairdresser or Salon Staff
                        </SelectItem>
                        <SelectItem value="health_services_manager">
                          Health Services Manager
                        </SelectItem>
                        <SelectItem value="heavy_truck_and_lorry_driver">
                          Heavy Truck and Lorry Driver
                        </SelectItem>
                        <SelectItem value="hotel_or_resort_professional">
                          Hotel or Resort Professional
                        </SelectItem>
                        <SelectItem value="housewife">Housewife</SelectItem>
                        <SelectItem value="human_resource_manager_agriculture">
                          Human Resource Manager - Agriculture
                        </SelectItem>
                        <SelectItem value="human_resource_manager_construction">
                          Human Resource Manager - Construction
                        </SelectItem>
                        <SelectItem value="human_resource_manager_manufacturing">
                          Human Resource Manager - Manufacturing
                        </SelectItem>
                        <SelectItem value="human_resource_manager_plantation">
                          Human Resource Manager - Plantation
                        </SelectItem>
                        <SelectItem value="human_resource_manager_services">
                          Human Resource Manager - Services
                        </SelectItem>
                        <SelectItem value="immigration_or_customs_officer_assistant">
                          Immigration or Customs Officer/Assistant
                        </SelectItem>
                        <SelectItem value="information_and_communications_technology_manager">
                          Information and Communications Technology Manager
                        </SelectItem>
                        <SelectItem value="insurance_agent">
                          Insurance Agent
                        </SelectItem>
                        <SelectItem value="lifting_truck_operator">
                          Lifting Truck Operator
                        </SelectItem>
                        <SelectItem value="livestock_farm_labourer">
                          Livestock Farm Labourer
                        </SelectItem>
                        <SelectItem value="locomotive_engine_driver">
                          Locomotive Engine Driver
                        </SelectItem>
                        <SelectItem value="manufacturing_supervisor">
                          Manufacturing Supervisor
                        </SelectItem>
                        <SelectItem value="manufacturing_worker">
                          Manufacturing Worker
                        </SelectItem>
                        <SelectItem value="masseur">Masseur</SelectItem>
                        <SelectItem value="mixed_crop_and_livestock_farm_labourer">
                          Mixed Crop and Livestock farm Labourer
                        </SelectItem>
                        <SelectItem value="mixed_crop_grower">
                          Mixed Crop Grower
                        </SelectItem>
                        <SelectItem value="musician">Musician</SelectItem>
                        <SelectItem value="navy">Navy</SelectItem>
                        <SelectItem value="personal_care_worker_in_health_services_not_elsewhere_classified">
                          Personal Care Worker in Health Services not elsewhere
                          Classified
                        </SelectItem>
                        <SelectItem value="petroleum_and_natural_gas_refining_plant_operator">
                          Petroleum and Natural Gas Refining Plant Operator
                        </SelectItem>
                        <SelectItem value="plantation_worker">
                          Plantation Worker
                        </SelectItem>
                        <SelectItem value="police_officer">
                          Police Officer
                        </SelectItem>
                        <SelectItem value="pre_press_worker">
                          Pre-Press Worker
                        </SelectItem>
                        <SelectItem value="primary_school">
                          Primary School
                        </SelectItem>
                        <SelectItem value="print_finishing_and_binding_worker">
                          Print Finishing and Binding Worker
                        </SelectItem>
                        <SelectItem value="printer">Printer</SelectItem>
                        <SelectItem value="printing_and_photo_engravers_and_etcher">
                          Printing and Photo Engravers and Etcher
                        </SelectItem>
                        <SelectItem value="prison_guard">
                          Prison Guard
                        </SelectItem>
                        <SelectItem value="protective_services_worker_not_elsewhere_classified">
                          Protective Services Worker not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="receptionist">
                          Receptionist
                        </SelectItem>
                        <SelectItem value="regulatory_government_professional_not_elsewhere_classified">
                          Regulatory Government Professional not elsewhere
                          Classified
                        </SelectItem>
                        <SelectItem value="restaurant_professional">
                          Restaurant Professional
                        </SelectItem>
                        <SelectItem value="retail_and_wholesale_trade_manager">
                          Retail and Wholesale Trade Manager
                        </SelectItem>
                        <SelectItem value="retiree">Retiree</SelectItem>
                        <SelectItem value="sales_and_marketing_manager">
                          Sales and Marketing Manager
                        </SelectItem>
                        <SelectItem value="sales_worker_not_elsewhere_classified">
                          Sales Worker not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="secondary_education_teacher">
                          Secondary Education Teacher
                        </SelectItem>
                        <SelectItem value="security_guard">
                          Security Guard
                        </SelectItem>
                        <SelectItem value="security_manager">
                          Security Manager
                        </SelectItem>
                        <SelectItem value="senior_government_officials">
                          Senior Government Officials
                        </SelectItem>
                        <SelectItem value="ship_deck_crews_and_related_worker">
                          Ship Deck Crews and Related Worker
                        </SelectItem>
                        <SelectItem value="ship_deck_officer_and_pilot">
                          Ship Deck Officer and Pilot
                        </SelectItem>
                        <SelectItem value="ship_engineer">
                          Ship Engineer
                        </SelectItem>
                        <SelectItem value="shop_assistant">
                          Shop Assistant
                        </SelectItem>
                        <SelectItem value="shop_supervisor">
                          Shop Supervisor
                        </SelectItem>
                        <SelectItem value="shopkeeper">Shopkeeper</SelectItem>
                        <SelectItem value="shrub_crop_grower">
                          Shrub Crop Grower
                        </SelectItem>
                        <SelectItem value="social_welfare_manager">
                          Social Welfare Manager
                        </SelectItem>
                        <SelectItem value="social_work_associate_professional">
                          Social Work Associate Professional
                        </SelectItem>
                        <SelectItem value="sports_and_recreation_centre_manager">
                          Sports and Recreation Centre Manager
                        </SelectItem>
                        <SelectItem value="sports_coach">
                          Sports Coach
                        </SelectItem>
                        <SelectItem value="stall_and_market_salesperson">
                          Stall and Market Salesperson
                        </SelectItem>
                        <SelectItem value="street_and_related_service_worker">
                          Street and Related Service Worker
                        </SelectItem>
                        <SelectItem value="street_food_salesperson">
                          Street Food Salesperson
                        </SelectItem>
                        <SelectItem value="street_vendors_or_stall_vendor">
                          Street Vendors or Stall Vendor
                        </SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                        <SelectItem value="surface_and_window_cleaner">
                          Surface and Window Cleaner
                        </SelectItem>
                        <SelectItem value="teaching_professional_not_elsewhere_classified">
                          Teaching Professional not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="telecommunications_engineer">
                          Telecommunications Engineer
                        </SelectItem>
                        <SelectItem value="tourist">Tourist</SelectItem>
                        <SelectItem value="transport_conductor">
                          Transport Conductor
                        </SelectItem>
                        <SelectItem value="travel_attendant_and_travel_steward">
                          Travel Attendant and Travel Steward
                        </SelectItem>
                        <SelectItem value="travel_consultant_and_related_clerk">
                          Travel Consultant and Related Clerk
                        </SelectItem>
                        <SelectItem value="travel_guide_or_agent">
                          Travel Guide or Agent
                        </SelectItem>
                        <SelectItem value="university_and_higher_education_teaching_professional">
                          University and Higher Education Teaching Professional
                        </SelectItem>
                        <SelectItem value="vegetable_grower">
                          Vegetable Grower
                        </SelectItem>
                        <SelectItem value="waiter">Waiter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Employer Name and Nature of Business */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-1">
                    <Label htmlFor="employerName">Employer Name</Label>
                    <Input
                      id="employerName"
                      name="employerName"
                      placeholder="Company where you work"
                      value={formData.employerName}
                      onChange={handleInputChange}
                      className="pl-2"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="natureOfBusiness">Nature of Business</Label>
                    <Input
                      id="natureOfBusiness"
                      name="natureOfBusiness"
                      placeholder="e.g., Technology"
                      value={formData.natureOfBusiness}
                      onChange={handleInputChange}
                      className="pl-2"
                    />
                  </div>
                </div>
              </div>

              {/* Financial Information Section - Gap before this section */}
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3">
                  Financial Information
                </h3>

                {/* Purpose of Account and Source of Funds */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-1">
                    <Label htmlFor="purposeOfAccount">Purpose of Account</Label>
                    <Select
                      name="purposeOfAccount"
                      value={formData.purposeOfAccount}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          purposeOfAccount: value,
                        }))
                      }
                    >
                      <SelectTrigger id="purposeOfAccount">
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Personal">Personal Use</SelectItem>
                        <SelectItem value="Business">Business Use</SelectItem>
                        <SelectItem value="Savings">Savings</SelectItem>
                        <SelectItem value="Investment">Investment</SelectItem>
                        <SelectItem value="Travel">Travel</SelectItem>
                        <SelectItem value="Education">Education</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="sourceOfFunds">Source of Funds</Label>
                    <Select
                      name="sourceOfFunds"
                      value={formData.sourceOfFunds}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          sourceOfFunds: value,
                        }))
                      }
                    >
                      <SelectTrigger id="sourceOfFunds">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Personal Sources */}
                        <SelectItem value="salary">Salary/Wages</SelectItem>
                        <SelectItem value="business income">
                          Business Income
                        </SelectItem>
                        <SelectItem value="investment">
                          Investment Returns
                        </SelectItem>
                        <SelectItem value="personal savings">
                          Personal Savings
                        </SelectItem>
                        <SelectItem value="inheritance">Inheritance</SelectItem>
                        <SelectItem value="gift">
                          Gift/Family Support
                        </SelectItem>
                        {/* Business/Corporate Sources */}
                        <SelectItem value="debt">Debt</SelectItem>
                        <SelectItem value="debt capital">
                          Debt Capital
                        </SelectItem>
                        <SelectItem value="directors capital">
                          Directors Capital
                        </SelectItem>
                        <SelectItem value="equity capital">
                          Equity Capital
                        </SelectItem>
                        <SelectItem value="term loan">Term Loan</SelectItem>
                        <SelectItem value="venture funding">
                          Venture Funding
                        </SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Volume of Assets */}
                <div className="space-y-1">
                  <Label htmlFor="volumeOfAssets">Volume of Assets</Label>
                  <Select
                    name="volumeOfAssets"
                    value={formData.volumeOfAssets}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        volumeOfAssets: value,
                      }))
                    }
                  >
                    <SelectTrigger id="volumeOfAssets">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20000">20,000</SelectItem>
                      <SelectItem value="20000_45000">
                        20,000 - 45,000
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/home")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading} className="flex-1">
                  {isLoading ? "Updating..." : "Update Profile"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <CountrySelectDialog
        open={countryDialogOpen}
        onOpenChange={setCountryDialogOpen}
        onSelect={handleCountrySelect}
        selectedCountry={selectedCountry}
      />

      {/* Loading Dialog */}
      <Dialog open={isLoadingData} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading Profile
            </DialogTitle>
            <DialogDescription>
              Loading your profile information, please wait...
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
